import { costBreakdownByStrategy } from '../data';

const costRows = ['Commission', 'Spread', 'Swap', 'Slippage (est.)'] as const;

const roleBgMap: Record<string, string> = {
  primary: 'bg-primary-50/50',
  accent: 'bg-accent-50/50',
  secondary: 'bg-secondary-50/50',
  foreground: 'bg-foreground-50/50',
};

const roleTextMap: Record<string, string> = {
  primary: 'text-primary-600',
  accent: 'text-accent-600',
  secondary: 'text-secondary-600',
  foreground: 'text-foreground-600',
};

const roles = ['primary', 'accent', 'secondary', 'foreground'] as const;

export default function CostBreakdownSection() {
  return (
    <section className="relative w-full bg-background-100 overflow-hidden">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-18">

        {/* Header */}
        <div className="mb-10 md:mb-14">
          <h2 className="text-[28px] md:text-[36px] font-[750] leading-[36px] md:leading-[44px] text-foreground-950 tracking-[-0.015em] max-w-[560px]">
            Where every dollar goes.
          </h2>
          <p className="mt-3 text-[15px] leading-[24px] text-foreground-500 max-w-[480px]">
            Four trader profiles. One clean ledger. See exactly what eats your profit.
          </p>
        </div>

        {/* Desktop: Comparison Table */}
        <div className="hidden md:block">
          <div className="border-t border-background-200">
            {/* Table Header */}
            <div className="grid grid-cols-[1fr_repeat(4,1fr)]">
              <div className="py-5 px-4"></div>
              {costBreakdownByStrategy.map((item, idx) => (
                <div key={idx} className="py-5 px-4 text-center border-l border-background-200">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-foreground-400 mb-2">
                    {item.strategy}
                  </div>
                  <div className="text-[32px] font-[750] text-foreground-950 leading-none tabular-nums">
                    ${item.totalMonthly.toLocaleString()}
                  </div>
                  <div className="text-[12px] text-foreground-500 mt-1.5">
                    {item.trader}
                  </div>
                </div>
              ))}
            </div>

            {/* Cost Rows */}
            {costRows.map((costName) => (
              <div key={costName} className="grid grid-cols-[1fr_repeat(4,1fr)] border-t border-background-200">
                <div className="py-4 px-4 flex items-center">
                  <span className="text-[14px] font-semibold text-foreground-700">
                    {costName.replace(' (est.)', '')}
                  </span>
                </div>
                {costBreakdownByStrategy.map((item, idx) => {
                  const cost = item.costs.find((c) => c.name === costName);
                  const maxPct = Math.max(...item.costs.map((c) => c.pct));
                  const isDominant = cost && cost.pct === maxPct && cost.pct > 0;
                  const role = roles[idx];
                  return (
                    <div
                      key={idx}
                      className={`py-4 px-4 text-right border-l border-background-200 ${isDominant ? roleBgMap[role] : ''}`}
                    >
                      {cost && cost.monthly > 0 ? (
                        <div className="tabular-nums">
                          <div className="text-[16px] font-[700] text-foreground-900">
                            ${cost.monthly.toLocaleString()}
                          </div>
                          <div
                            className={`text-[12px] font-bold mt-0.5 ${isDominant ? roleTextMap[role] : 'text-foreground-400'}`}
                          >
                            {cost.pct}%
                          </div>
                        </div>
                      ) : (
                        <span className="text-[14px] text-foreground-300">—</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Annual Row */}
            <div className="grid grid-cols-[1fr_repeat(4,1fr)] border-t-2 border-background-300">
              <div className="py-4 px-4 flex items-center">
                <span className="text-[13px] font-[700] text-foreground-950">Annual total</span>
              </div>
              {costBreakdownByStrategy.map((item, idx) => (
                <div key={idx} className="py-4 px-4 text-right border-l border-background-200">
                  <div className="text-[16px] font-[700] text-foreground-700 tabular-nums">
                    ${item.totalAnnual.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Insights row */}
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-0 border-t border-background-200">
            {costBreakdownByStrategy.map((item, idx) => {
              const role = roles[idx];
              return (
                <div
                  key={idx}
                  className={`py-5 px-4 ${idx > 0 ? 'border-l border-background-200' : ''}`}
                >
                  <div className={`text-[11px] font-bold uppercase tracking-wider ${roleTextMap[role]} mb-2`}>
                    {item.strategy}
                  </div>
                  <p className="text-[13px] font-[700] text-foreground-950 leading-snug">
                    {item.keyInsight}
                  </p>
                  <p className="text-[12px] text-foreground-500 mt-1.5 leading-relaxed">
                    {item.costAsPctOfTarget}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: Vertical invoices */}
        <div className="md:hidden space-y-10">
          {costBreakdownByStrategy.map((item, idx) => {
            const role = roles[idx];
            const maxPct = Math.max(...item.costs.map((c) => c.pct));
            return (
              <div key={idx} className="border-t-2 border-background-300 pt-5">
                <div className="flex justify-between items-start mb-5">
                  <div>
                    <div className={`text-[11px] font-bold uppercase tracking-wider ${roleTextMap[role]} mb-1`}>
                      {item.strategy}
                    </div>
                    <div className="text-[14px] font-semibold text-foreground-700">{item.trader}</div>
                    <div className="text-[11px] text-foreground-400 mt-1">{item.assumptions}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[28px] font-[750] text-foreground-950 leading-none tabular-nums">
                      ${item.totalMonthly.toLocaleString()}
                    </div>
                    <div className="text-[11px] text-foreground-400 mt-1">per month</div>
                  </div>
                </div>

                <div className="divide-y divide-background-200">
                  {item.costs
                    .filter((c) => c.monthly > 0)
                    .sort((a, b) => b.pct - a.pct)
                    .map((cost, ci) => {
                      const isDominant = cost.pct === maxPct;
                      return (
                        <div
                          key={ci}
                          className={`py-3 flex justify-between items-start ${isDominant ? roleBgMap[role] + ' -mx-3 px-3' : ''}`}
                        >
                          <div>
                            <div className="text-[14px] font-semibold text-foreground-700">
                              {cost.name.replace(' (est.)', '')}
                            </div>
                            <div className="text-[11px] text-foreground-400 mt-0.5">{cost.detail}</div>
                          </div>
                          <div className="text-right tabular-nums flex-shrink-0 ml-4">
                            <div className="text-[16px] font-[700] text-foreground-900">
                              ${cost.monthly.toLocaleString()}
                            </div>
                            <div
                              className={`text-[12px] font-bold ${isDominant ? roleTextMap[role] : 'text-foreground-400'}`}
                            >
                              {cost.pct}%
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>

                <div className="mt-5 pt-4 border-t border-background-200">
                  <p className="text-[13px] font-[700] text-foreground-950 leading-snug">
                    {item.keyInsight}
                  </p>
                  <p className="text-[12px] text-foreground-500 mt-1.5 leading-relaxed">
                    {item.costAsPctOfTarget}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom summary */}
        <div className="mt-14 md:mt-20 pt-8 border-t border-background-200">
          <div className="max-w-[800px]">
            <p className="text-[15px] font-[700] text-foreground-950 mb-2">The pattern is unmistakable</p>
            <p className="text-[14px] text-foreground-600 leading-relaxed">
              Scalping = commission-dominated. Day trading = balanced spread and commission.
              Swing trading = swap takes over. Position trading = swap is everything.
              Your strategy choice has already determined your single biggest cost
              — whether you know it or not.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}